import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Mail, Phone, Globe, ChevronDown, Droplets, FileText, BellRing, LifeBuoy } from "lucide-react";

const FAQS = [
  { q: "How do I activate a bottle?", a: "Go to Bottle Activations, enter the unique code printed on your SGBINOXE bottle, and confirm. The credits attached to that bottle are added to your account instantly and can be used to generate certificates." },
  { q: "How do I generate a certificate?", a: "Open Certificates → New Certificate, fill in the customer and vehicle details, and submit. One credit is deducted per certificate. A branded PDF is generated automatically and can be emailed to the customer." },
  { q: "How do follow-ups work?", a: "Every certificate schedules a customer follow-up 30 days after installation, with a second past-due reminder 10 days later (at 40 days) if no outcome is recorded. You'll be reminded by email and in your Follow-Up Center. Record an outcome for each one — you can also schedule your own custom reminders and log notes as you go." },
  { q: "What happens when I run low on credits?", a: "You'll see a warning on your dashboard. Activate another SGBINOXE bottle to top up your certificate credits." },
  { q: "Can customers verify a certificate?", a: "Yes. Every certificate carries a QR code and a public verification link that confirms authenticity without exposing private details." },
  { q: "I forgot my password. What do I do?", a: "Use the 'Forgot password' link on the login screen. You'll receive a secure reset link by email." },
];

function Accordion({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#141414]" data-testid="faq-item">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-4 text-left" data-testid="faq-toggle">
        <span className="text-sm font-medium text-white">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm leading-relaxed text-neutral-400">{a}</p>}
    </div>
  );
}

const GUIDES = [
  { icon: Droplets, title: "Activate a Bottle", steps: "Bottle Activations → enter code → confirm → credits added." },
  { icon: FileText, title: "Generate a Certificate", steps: "Certificates → New → fill customer & vehicle → submit → PDF + email." },
  { icon: BellRing, title: "Manage Follow-Ups", steps: "Follow-Up Center → open a due item → contact customer → update status & notes." },
];

export default function Help() {
  const [support, setSupport] = useState(null);
  useEffect(() => { api.get("/support-info").then(({ data }) => setSupport(data)).catch(() => {}); }, []);

  return (
    <DashboardLayout title="Help Center">
      <div className="mb-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Support</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">How can we help?</h2>
        <p className="mt-1 text-sm text-neutral-500">Guides, answers, and ways to reach the SGBINOXE team.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {GUIDES.map((g) => (
          <div key={g.title} className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="help-guide">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E30613]/[0.12]"><g.icon className="h-5 w-5 text-[#FF1C2A]" /></div>
            <h3 className="mt-4 font-display text-base font-medium text-white">{g.title}</h3>
            <p className="mt-2 text-sm text-neutral-500">{g.steps}</p>
          </div>
        ))}
      </div>

      <h3 className="mb-4 mt-10 font-display text-base font-medium text-white">Frequently asked questions</h3>
      <div className="space-y-3">
        {FAQS.map((f) => (<Accordion key={f.q} {...f} />))}
      </div>

      <div className="mt-10 rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="contact-support">
        <div className="mb-5 flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-[#FF1C2A]" /><h3 className="font-display text-base font-medium text-white">Contact Support</h3></div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <a href={`mailto:${support?.support_email || ""}`} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#0A0A0A] px-4 py-3 hover:bg-white/[0.03]" data-testid="support-email">
            <Mail className="h-4 w-4 text-neutral-400" /><div><p className="text-[11px] uppercase tracking-wider text-neutral-600">Email</p><p className="text-sm text-white">{support?.support_email || "—"}</p></div>
          </a>
          <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#0A0A0A] px-4 py-3" data-testid="support-phone">
            <Phone className="h-4 w-4 text-neutral-400" /><div><p className="text-[11px] uppercase tracking-wider text-neutral-600">Phone</p><p className="text-sm text-white">{support?.support_phone || "—"}</p></div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#0A0A0A] px-4 py-3" data-testid="support-website">
            <Globe className="h-4 w-4 text-neutral-400" /><div><p className="text-[11px] uppercase tracking-wider text-neutral-600">Website</p><p className="text-sm text-white">{support?.website || "—"}</p></div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
